/**
 * <machine-strip> — the homepage's finder, or the garage when there is one.
 *
 * With no machine saved the strip is the finder. With machines saved the
 * finder would only ask again what the garage already knows, so the strip
 * lists the garage instead (the <garage-panel> in `pick` mode) and keeps the
 * finder behind "Add another machine" (Lucas, 2026-10-10).
 *
 * The section's inline script sets data-has-machines from localStorage
 * before the finder is parsed, so there is no flash; this keeps the mark
 * true to the garage from then on, and runs the add button.
 */
class MachineStrip extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.add = this.querySelector('[data-strip-add]');
    this.finder = this.querySelector('[data-strip-finder]');
    this.addLabel = this.add ? this.add.textContent.trim() : '';

    this.add?.addEventListener('click', () => this.toggleFinder(!this.hasAttribute('data-adding')));

    this.onChange = () => this.paint();
    document.addEventListener('garage:changed', this.onChange);
    this.paint();
  }

  disconnectedCallback() {
    document.removeEventListener('garage:changed', this.onChange);
  }

  paint() {
    const has = (window.Garage?.count ?? 0) > 0;
    this.toggleAttribute('data-has-machines', has);
    // Without a machine the finder is the strip, so there is nothing to add to.
    if (!has) this.toggleFinder(false);
  }

  /** "Add another machine" reveals the finder beneath the list, and folds it away again. */
  toggleFinder(open) {
    this.toggleAttribute('data-adding', open);
    if (this.add) {
      this.add.setAttribute('aria-expanded', String(open));
      this.add.textContent = open ? 'Cancel' : this.addLabel;
    }
    if (open) this.finder?.querySelector('select')?.focus();
  }
}

if (!customElements.get('machine-strip')) {
  customElements.define('machine-strip', MachineStrip);
}
